/**
 * Training Knowledge Base document-management API.
 *
 * Wires the KB page to the workspace-scoped backend built in
 * ourteam-backend#3086 (BE ticket #3112). Endpoints (all under
 * `/training/knowledge-base`, scoped to the caller's training workspace):
 *   - GET    /documents            list (page, page_size, search, uploaded_by, scope, campaign_id) → { documents, total, stats }
 *   - POST   /documents            multipart `files` upload (+ optional scope_type/scope_id) → { results: per-file completed|failed }
 *   - DELETE /documents/:id        remove a document (204)
 *   - GET    /documents/:id/file   serves the original (redirects to a presigned S3 URL)
 *   - GET    /uploaders            [{ id, name, count }] for the uploader filter
 *
 * Scoped-knowledge endpoints (BE: Trainer KB scoping Phase 1 — see
 * docs/TRAINING_KB_SCOPES_BE_HANDOFF_2026-07-16.md; gated behind
 * VITE_ENABLE_TRAINING_KB_SCOPES until the BE ships):
 *   - GET    /training/campaigns/:id/knowledge-base                       → { attached, inherited (with excluded flag) }
 *   - POST   /training/campaigns/:id/knowledge-base/attachments           { document_ids } attach existing docs
 *   - DELETE /training/campaigns/:id/knowledge-base/attachments/:doc_id   detach (doc itself is kept)
 *   - POST   /training/campaigns/:id/knowledge-base/exclusions/:doc_id    exclude an inherited workspace doc from THIS campaign
 *   - DELETE /training/campaigns/:id/knowledge-base/exclusions/:doc_id    re-include a previously excluded workspace doc
 *
 * The BE speaks snake_case; this layer is the thin mapper to the camelCase
 * `KbDocument` the page renders. `preview_url` is a presigned, inline-
 * disposition S3 URL (1h) that goes straight into the preview `<iframe src>`;
 * `file_url` is the original-file route used for download / fallback.
 *
 * RBAC is enforced BE-side: only Owner/Admin may upload/delete (others get
 * 403); all roles may list/preview.
 */
import axios from "axios";
import { getCookie } from "@util/cookie";
import type { RubricScores } from "@type/training";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/**
 * Feature flag for CAMPAIGN-scoped knowledge (campaign attachments/exclusions,
 * scope column and filter, campaign wizard/detail Knowledge surfaces). The
 * endpoints behind it don't exist until the BE Phase 1 ships, so it defaults
 * off; the Sources card is NOT behind this flag — it keys off the presence of
 * `knowledge_manifest` in result payloads instead.
 */
export const KB_SCOPES_ENABLED =
    import.meta.env.VITE_ENABLE_TRAINING_KB_SCOPES === "true";

/**
 * Separate flag for AIQM UPLOAD-scoped knowledge (the upload dialog's "Add
 * knowledge documents" step + `knowledge_files`/`excluded_knowledge_document_ids`
 * on the upload request). Kept independent of KB_SCOPES_ENABLED because the BE
 * that consumes those fields (Phase 2, ourteam-backend#3583) ships separately —
 * enabling campaign KB must NOT silently expose an AIQM feature the backend
 * still ignores. Defaults off.
 */
export const KB_AIQM_BATCH_KNOWLEDGE_ENABLED =
    import.meta.env.VITE_ENABLE_TRAINING_AIQM_KB_SCOPES === "true";

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

// ----- FE (camelCase) shapes the page consumes -----

/** One of the 5 scoring categories a document can inform. */
export type KbScoringCategory = keyof RubricScores;

/** A fact the AI extracted from a document, tagged with the category it informs.
 *  Populated by the separate BE extraction task; absent on today's responses. */
export interface KbExtractedFact {
    fact: string;
    element: KbScoringCategory;
}

export interface KbUploadedBy {
    id: string;
    name: string;
}

/** Where a document is attached. A document is one stored blob; scope lives on
 *  the attachment, so the same doc can appear at several scopes (dedup'd by
 *  content hash BE-side). Absent `scopes` ⇒ workspace-wide (legacy responses). */
export type KbScopeType = "workspace" | "campaign" | "aiqm_batch";

export interface KbScope {
    type: KbScopeType;
    /** Campaign / batch id — absent for workspace scope. */
    id?: string;
    /** Human label (e.g. campaign name) for scope chips. */
    name?: string;
}

export interface KbDocument {
    id: string;
    name: string;
    fileType: "PDF" | "DOCX";
    sizeBytes: number;
    uploadedBy: KbUploadedBy;
    uploadedAt: string;
    /** "processing" while ingestion finishes; "completed" once ready. */
    status: "completed" | "processing" | "failed";
    /** Presigned, inline-embeddable preview URL (BE `preview_url`) — drives the in-drawer PDF preview. */
    previewUrl?: string;
    /** Original-file URL (BE `file_url`); may be the auth-protected /documents/:id/file route. */
    fileUrl?: string;
    // Extraction surfaces (BE #3140) — populated by the BE extraction task.
    /** Extraction pipeline state, distinct from `status` (file ingestion). */
    extractionStatus?: "pending" | "processing" | "completed" | "failed";
    /** One-line AI summary of the document (BE `summary`). */
    summary?: string;
    influences?: KbScoringCategory[];
    facts?: KbExtractedFact[];
    /** Attachment scopes (Phase 1). Absent on legacy responses ⇒ workspace. */
    scopes?: KbScope[];
    /** Only on campaign-knowledge `inherited` docs: this workspace doc was
     *  explicitly excluded from the campaign (it applies by default otherwise). */
    excluded?: boolean;
}

export interface KbUploader {
    id: string;
    name: string;
    count?: number;
}

export interface KbStats {
    /** Workspace-wide (NOT affected by filters) — backs the two stat tiles. */
    totalDocuments: number;
    storageUsedBytes: number;
}

export interface KbListResult {
    documents: KbDocument[];
    /** Filtered count, for pagination. */
    total: number;
    stats: KbStats;
}

// ----- API (snake_case) shapes -----

interface ApiUploadedBy {
    id?: string;
    name?: string;
}

interface ApiKbDocument {
    id: string | number;
    name?: string;
    file_type?: string;
    size_bytes?: number;
    uploaded_by?: ApiUploadedBy | string;
    uploaded_at?: string;
    status?: string;
    extraction_status?: string;
    summary?: string;
    preview_url?: string;
    file_url?: string;
    influences?: string[];
    facts?: { fact?: string; element?: string }[];
    scopes?: { type?: string; id?: string | number; name?: string }[];
    excluded?: boolean;
}

function mapFileType(raw?: string): "PDF" | "DOCX" {
    return (raw ?? "").toLowerCase() === "docx" ? "DOCX" : "PDF";
}

function mapUploadedBy(raw: ApiKbDocument["uploaded_by"]): KbUploadedBy {
    if (raw && typeof raw === "object") {
        return { id: raw.id ?? "", name: raw.name ?? "Unknown" };
    }
    if (typeof raw === "string") return { id: "", name: raw };
    return { id: "", name: "Unknown" };
}

function mapStatus(raw?: string): KbDocument["status"] {
    if (raw === "processing") return "processing";
    if (raw === "failed") return "failed";
    return "completed";
}

function mapExtractionStatus(raw?: string): KbDocument["extractionStatus"] {
    return raw === "pending" || raw === "processing" || raw === "completed" || raw === "failed"
        ? raw
        : undefined;
}

/** The 5 scoring categories (keyof RubricScores, BE #3140) a fact/influence may reference. */
const KB_SCORING_CATEGORIES: KbScoringCategory[] = [
    "empathy",
    "problem_resolution",
    "product_knowledge",
    "tone",
    "call_handling",
];
function isScoringCategory(v: unknown): v is KbScoringCategory {
    return typeof v === "string" && (KB_SCORING_CATEGORIES as string[]).includes(v);
}

function mapDocument(raw: ApiKbDocument): KbDocument {
    const doc: KbDocument = {
        id: String(raw.id),
        name: typeof raw.name === "string" && raw.name.length > 0 ? raw.name : "Untitled",
        fileType: mapFileType(raw.file_type),
        sizeBytes: typeof raw.size_bytes === "number" ? raw.size_bytes : 0,
        uploadedBy: mapUploadedBy(raw.uploaded_by),
        uploadedAt: typeof raw.uploaded_at === "string" ? raw.uploaded_at : "",
        status: mapStatus(raw.status),
        extractionStatus: mapExtractionStatus(raw.extraction_status),
        summary:
            typeof raw.summary === "string" && raw.summary.length > 0
                ? raw.summary
                : undefined,
        previewUrl:
            typeof raw.preview_url === "string" && raw.preview_url.length > 0
                ? raw.preview_url
                : undefined,
        fileUrl:
            typeof raw.file_url === "string" && raw.file_url.length > 0
                ? raw.file_url
                : undefined,
    };
    // Extraction fields (BE #3140), defensively mapped + validated against the 5
    // scoring categories so the "Extracted Information" tab + "Informs" column
    // light up once the BE returns them, and a stray `element` can't break the
    // RUBRIC_LABELS lookup.
    if (Array.isArray(raw.influences)) {
        const valid = raw.influences.filter(isScoringCategory);
        if (valid.length > 0) doc.influences = valid;
    }
    if (Array.isArray(raw.facts)) {
        const valid = raw.facts
            .filter(
                (f): f is { fact: string; element: KbScoringCategory } =>
                    typeof f?.fact === "string" && isScoringCategory(f?.element),
            )
            .map((f) => ({ fact: f.fact, element: f.element }));
        if (valid.length > 0) doc.facts = valid;
    }
    if (Array.isArray(raw.scopes)) {
        const valid: KbScope[] = raw.scopes
            .filter(
                (s): s is { type: KbScopeType; id?: string | number; name?: string } =>
                    s?.type === "workspace" || s?.type === "campaign" || s?.type === "aiqm_batch",
            )
            .map((s) => ({
                type: s.type,
                ...(s.id !== undefined && s.id !== null ? { id: String(s.id) } : {}),
                ...(typeof s.name === "string" && s.name.length > 0 ? { name: s.name } : {}),
            }));
        if (valid.length > 0) doc.scopes = valid;
    }
    if (raw.excluded === true) doc.excluded = true;
    return doc;
}

// ----- 1. List -----

export interface KbListParams {
    page?: number;
    page_size?: number;
    search?: string;
    uploaded_by?: string;
    /** Phase 1: restrict to one scope type ("workspace" | "campaign"). Omit for all. */
    scope?: Extract<KbScopeType, "workspace" | "campaign">;
    /** Phase 1: restrict to documents attached to one campaign. */
    campaign_id?: string;
}

export async function listKbDocuments(
    params: KbListParams = {},
): Promise<KbListResult> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    if (params.search) query.set("search", params.search);
    if (params.uploaded_by) query.set("uploaded_by", params.uploaded_by);
    if (params.scope) query.set("scope", params.scope);
    if (params.campaign_id) query.set("campaign_id", params.campaign_id);

    const qs = query.toString();
    const res = await axios.get(
        `${apiURL}/training/knowledge-base/documents${qs ? `?${qs}` : ""}`,
        { headers: authHeaders() },
    );

    // Accept both the documented top-level `{ documents, total, stats }` and the
    // platform's `{ data: { ... } }` envelope, so neither shape breaks the page.
    const body = (res.data?.data ?? res.data ?? {}) as {
        documents?: ApiKbDocument[];
        total?: number;
        stats?: { total_documents?: number; storage_used_bytes?: number };
    };
    const documents = Array.isArray(body.documents)
        ? body.documents.map(mapDocument)
        : [];
    const total = typeof body.total === "number" ? body.total : documents.length;
    const rawStats = body.stats ?? {};
    const stats: KbStats = {
        totalDocuments:
            typeof rawStats.total_documents === "number"
                ? rawStats.total_documents
                : total,
        storageUsedBytes:
            typeof rawStats.storage_used_bytes === "number"
                ? rawStats.storage_used_bytes
                : documents.reduce((s, d) => s + d.sizeBytes, 0),
    };
    return { documents, total, stats };
}

/**
 * Fetch ALL documents across pages for selection UIs (attach/exclude pickers,
 * the wizard/upload knowledge steps). `listKbDocuments` is paginated at 100/
 * page and those surfaces filter client-side, so a workspace with >100 docs
 * would silently hide everything past the first page. This exhausts pagination.
 *
 * Guarded against a misbehaving backend: stops when the accumulated count
 * reaches `total`, when a page comes back empty, or at a hard page cap.
 */
export async function listAllKbDocuments(
    params: Omit<KbListParams, "page" | "page_size"> = {},
): Promise<KbDocument[]> {
    const PAGE_SIZE = 100;
    const MAX_PAGES = 50; // 5,000 docs — defensive cap against a runaway loop
    const all: KbDocument[] = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
        const res = await listKbDocuments({ ...params, page, page_size: PAGE_SIZE });
        all.push(...res.documents);
        if (res.documents.length === 0 || all.length >= res.total) break;
    }
    return all;
}

// ----- 2. Upload -----

export type KbUploadFileStatus = "completed" | "processing" | "failed";

export interface KbUploadFileResult {
    status: KbUploadFileStatus;
    filename: string;
    document?: KbDocument;
    error?: string;
}

export interface KbUploadResult {
    results: KbUploadFileResult[];
}

interface ApiUploadResultItem {
    status?: string;
    filename?: string;
    error?: string;
    document?: ApiKbDocument;
}

/** Attach freshly uploaded files to a narrower scope than the workspace
 *  default (Phase 1). Omitted ⇒ workspace-wide, byte-identical to today. */
export interface KbUploadScope {
    type: Extract<KbScopeType, "campaign" | "aiqm_batch">;
    id: string;
}

/**
 * Upload one or more PDF files in a single multipart request (field `files`).
 * PDF only is a product decision (2026-07-16): DOCX was accepted but never
 * text-extracted BE-side, so it silently contributed nothing to evaluation —
 * the FE restricts to PDF everywhere and the BE will 415 DOCX in KB Phase 0.
 * Returns a per-file result so the toast can mark each file done/failed. BE
 * enforces the type check (415) and the 100 MB total cap (413); non-Owner/Admin
 * gets 403 — all of these reject the whole request and surface as a thrown
 * error the caller turns into "all failed".
 *
 * `scope` (Phase 1) adds `scope_type`/`scope_id` form fields so the new docs
 * attach to a campaign instead of the workspace; legacy BEs ignore unknown
 * form fields, so sending it before Phase 1 ships degrades to workspace scope.
 */
export async function uploadKbDocuments(
    files: File[],
    onProgress?: (percent: number) => void,
    scope?: KbUploadScope,
): Promise<KbUploadResult> {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    if (scope) {
        formData.append("scope_type", scope.type);
        formData.append("scope_id", scope.id);
    }

    const token = getCookie("authToken");
    const res = await axios.post(
        `${apiURL}/training/knowledge-base/documents`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 300_000,
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        },
    );

    const body = (res.data?.data ?? res.data ?? {}) as { results?: ApiUploadResultItem[] };
    const rawResults = Array.isArray(body.results) ? body.results : [];
    const results: KbUploadFileResult[] = rawResults.map((r) => {
        const status: KbUploadFileStatus =
            r.status === "failed"
                ? "failed"
                : r.status === "processing"
                  ? "processing"
                  : "completed";
        const document = r.document ? mapDocument(r.document) : undefined;
        const filename =
            typeof r.filename === "string" && r.filename.length > 0
                ? r.filename
                : (document?.name ?? "");
        return {
            status,
            filename,
            document,
            error: typeof r.error === "string" ? r.error : undefined,
        };
    });
    return { results };
}

// ----- 1b. Get one document -----

/**
 * Fetch a single document (GET /documents/:id — exists BE-side since #3112).
 * Used by the "Knowledge used in this evaluation" card to open the shared
 * document drawer from a manifest entry, which only carries the id + name.
 */
export async function getKbDocument(id: string): Promise<KbDocument> {
    const res = await axios.get(
        `${apiURL}/training/knowledge-base/documents/${id}`,
        { headers: authHeaders() },
    );
    const body = (res.data?.data ?? res.data ?? {}) as ApiKbDocument;
    return mapDocument(body);
}

// ----- 2b. Campaign-scoped knowledge (Phase 1) -----

export interface CampaignKnowledge {
    /** Documents attached to this campaign specifically. */
    attached: KbDocument[];
    /** Workspace-wide documents that also apply, shown read-only. */
    inherited: KbDocument[];
}

/**
 * The campaign's effective knowledge set, split into its own attachments and
 * the inherited workspace documents — both are shown wherever a user manages
 * campaign knowledge so what they see matches what the evaluator will get.
 */
export async function getCampaignKnowledge(
    campaignId: string,
): Promise<CampaignKnowledge> {
    const res = await axios.get(
        `${apiURL}/training/campaigns/${campaignId}/knowledge-base`,
        { headers: authHeaders() },
    );
    const body = (res.data?.data ?? res.data ?? {}) as {
        attached?: ApiKbDocument[];
        inherited?: ApiKbDocument[];
    };
    return {
        attached: Array.isArray(body.attached) ? body.attached.map(mapDocument) : [],
        inherited: Array.isArray(body.inherited) ? body.inherited.map(mapDocument) : [],
    };
}

/** Attach existing KB documents to a campaign (idempotent BE-side). */
export async function attachCampaignKnowledge(
    campaignId: string,
    documentIds: string[],
): Promise<void> {
    await axios.post(
        `${apiURL}/training/campaigns/${campaignId}/knowledge-base/attachments`,
        { document_ids: documentIds },
        { headers: authHeaders() },
    );
}

/**
 * Detach a document from a campaign. The document itself is kept (it may be
 * attached elsewhere); this only removes it from the campaign's future
 * evaluations — completed results keep their manifest.
 */
export async function detachCampaignKnowledge(
    campaignId: string,
    documentId: string,
): Promise<void> {
    await axios.delete(
        `${apiURL}/training/campaigns/${campaignId}/knowledge-base/attachments/${documentId}`,
        { headers: authHeaders() },
    );
}

/**
 * Exclude an inherited workspace document from ONE campaign. Workspace docs
 * apply to every campaign by default; an exclusion is the per-campaign opt-out
 * (forward-only, like every knowledge edit — past results keep their manifest).
 */
export async function excludeCampaignKnowledge(
    campaignId: string,
    documentId: string,
): Promise<void> {
    await axios.post(
        `${apiURL}/training/campaigns/${campaignId}/knowledge-base/exclusions/${documentId}`,
        {},
        { headers: authHeaders() },
    );
}

/** Undo an exclusion — the workspace doc applies to this campaign again. */
export async function includeCampaignKnowledge(
    campaignId: string,
    documentId: string,
): Promise<void> {
    await axios.delete(
        `${apiURL}/training/campaigns/${campaignId}/knowledge-base/exclusions/${documentId}`,
        { headers: authHeaders() },
    );
}

// ----- 3. Delete -----

export async function deleteKbDocument(id: string): Promise<void> {
    await axios.delete(`${apiURL}/training/knowledge-base/documents/${id}`, {
        headers: authHeaders(),
    });
}

// ----- 4. Preview / download -----

/**
 * Fetch a document's original file as an object URL for the preview drawer.
 *
 * WHY this instead of putting `fileUrl` straight into the `<iframe src>`:
 * the BE's `file_url` is the **auth-protected** `/documents/:id/file` route
 * (it 307-redirects to a fresh presigned S3 URL). An `<iframe>` can't send the
 * `Authorization: Bearer` header, so embedding the URL directly returns
 * `{"detail":"Not authenticated"}`. We instead fetch with the header (axios
 * follows the cross-origin S3 redirect, which the browser completes without the
 * stripped auth header) and hand back a blob URL — the same pattern the agent
 * call-audio player uses (`getSessionAudioUrl`). The PDF mime is forced so the
 * iframe renders it inline rather than downloading it. Returns null on failure
 * so the caller can fall back to the "preview unavailable" state.
 */
export async function getKbDocumentPreviewUrl(
    fileUrl: string,
    fileType: "PDF" | "DOCX",
): Promise<string | null> {
    try {
        const token = getCookie("authToken");
        const res = await axios.get(fileUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            responseType: "blob",
            timeout: 60_000,
        });
        const mime =
            fileType === "PDF"
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        return URL.createObjectURL(new Blob([res.data], { type: mime }));
    } catch {
        return null;
    }
}

// ----- 5. Uploaders -----

/**
 * Distinct uploaders for the "All Uploaders" filter (mirrors the AIQM
 * uploaders endpoint). Errors propagate on purpose so the page can tell a
 * broken auth-scoped route from a legitimately empty list and hide the filter
 * rather than render an empty, misleading dropdown.
 */
export async function listKbUploaders(): Promise<KbUploader[]> {
    const res = await axios.get(`${apiURL}/training/knowledge-base/uploaders`, {
        headers: authHeaders(),
    });
    const data = res.data?.data ?? res.data;
    return Array.isArray(data)
        ? data
              .filter(
                  (u: unknown): u is KbUploader =>
                      !!u &&
                      typeof (u as KbUploader).id === "string" &&
                      typeof (u as KbUploader).name === "string",
              )
              .map((u) => ({
                  id: u.id,
                  name: u.name,
                  ...(typeof u.count === "number" &&
                  Number.isInteger(u.count) &&
                  u.count >= 0
                      ? { count: u.count }
                      : {}),
              }))
        : [];
}
